/**
 * @description Utility function to retry an async operation with exponential backoff.
 * Specifically designed for network retry scenarios with configurable delay and attempts.
 *
 * @param {() => Promise<T>} operation - The async operation to retry.
 * @param {Object} options - Retry configuration options.
 * @param {number} options.maxAttempts - Maximum number of retry attempts (default: 3).
 * @param {number} options.baseDelay - Base delay in milliseconds between retries (default: 1000).
 * @param {number} options.maxDelay - Maximum delay in milliseconds (default: 10000).
 * @param {(error: unknown) => boolean} options.shouldRetry - Function to determine if error should trigger retry.
 * @returns {Promise<T>} - The result of the successful operation.
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  options: {
    maxAttempts?: number;
    baseDelay?: number;
    maxDelay?: number;
    shouldRetry?: (error: unknown) => boolean;
  } = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    shouldRetry = () => true,
  } = options;

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Don't retry if we shouldn't or if this is the last attempt
      if (!shouldRetry(error) || attempt === maxAttempts) {
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);

      // Add some jitter to prevent thundering herd
      const jitter = Math.random() * 0.3 * delay;
      const finalDelay = delay + jitter;

      console.log(
        `Retry attempt ${attempt}/${maxAttempts} after ${Math.round(
          finalDelay
        )}ms`,
        error
      );

      await new Promise((resolve) => setTimeout(resolve, finalDelay));
    }
  }

  throw lastError;
}
